import React, { useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import api from "../../../../api/api";
import {
    addSchool as addAction,
    updateSchool as updateAction,
    removeSchools as removeAction,
    undoSchools as undoAction,
    clearSchools as clearAction,
    fetchSchoolDetails,
} from "../actions";

export const useSchoolActions = ({ handleSearchSync }) => {
    const dispatch = useDispatch();

    const [activeModal, setActiveModal] = useState(null); // 'add' | 'edit' | 'bills' | 'notes' | 'info' | null
    const [selectedSchoolId, setSelectedSchoolId] = useState(null);
    const schoolsList = useSelector((state) => state.schools?.schools || []);
    const selectedSchool = schoolsList.find((s) => s.Id === selectedSchoolId) || null;
    const [schoolUsers, setSchoolUsers] = useState([]);
    const [loadingSchoolUsers, setLoadingSchoolUsers] = useState(false);
    const [activeAdminUser, setActiveAdminUser] = useState(null);

    const handleCloseModal = useCallback(() => {
        setActiveModal(null);
        setSelectedSchoolId(null);
        setSchoolUsers([]);
        setLoadingSchoolUsers(false);
    }, []);

    const fetchSchoolUsers = useCallback((schoolId, t) => {
        setSchoolUsers([]);
        setLoadingSchoolUsers(true);

        api.post("/users/", {
            SchoolId: schoolId,
            UserTypeId: 3,
            IsDeleted: false,
            Limit: 100,
        })
            .then((response) => {
                setSchoolUsers(response.data?.data || []);
                setLoadingSchoolUsers(false);
            })
            .catch(() => {
                setLoadingSchoolUsers(false);
                toast.error(t("Error.NoConnection.Title"));
            });
    }, []);

    // Delete school item API execution
    const executeDeleteItem = useCallback(
        (id, t, _toastId) => {
            toast.update(_toastId, {
                render: () => (
                    <div>
                        {" "}
                        <i className="fa-spin fas fa-spinner"></i> {t("Processing")}{" "}
                    </div>
                ),
                autoClose: false,
                closeOnClick: false,
            });

            api.delete(`/Schools/${id}`)
                .then(() => {
                    dispatch(clearAction(id));
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5">
                                    {" "}
                                    <i className="icon fal fa-exclamation-triangle"></i>{" "}
                                    {t("Pages.SchoolsControl.toast.Delete.Title")}
                                </div>{" "}
                                <div className="h6">{t("Pages.SchoolsControl.toast.Delete.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.SUCCESS,
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                    });
                })
                .catch(() => {
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5">
                                    {" "}
                                    <i className="icon fal fa-wifi-slash"></i> {t("Error.NoConnection.Title")}
                                </div>{" "}
                                <div className="h6">{t("Error.NoConnection.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                });
        },
        [dispatch]
    );

    // Show delete school modal dialog with undo
    const handleShowDelete = useCallback(
        (id, t) => {
            if (id) {
                dispatch(removeAction(id));

                let _toastId = toast(t("Processing"), {
                    position: "bottom-left",
                    autoClose: false,
                });

                toast.update(_toastId, {
                    render: () => (
                        <div>
                            <i className="fa-spin fas fa-spinner"></i> {t("DeleteProcessing")}
                            <div className="row row-cols-2 mt-2">
                                <div className="col">
                                    <button
                                        type="button"
                                        className="btn btn-danger btn-block"
                                        onClick={() => {
                                            dispatch(undoAction(id));
                                            toast.dismiss(_toastId);
                                        }}
                                    >
                                        <i className="icon fal fa-trash-undo"></i> {t("undo")}
                                    </button>
                                </div>
                                <div className="col">
                                    <button
                                        type="button"
                                        className="btn btn-light btn-block"
                                        onClick={() => {
                                            executeDeleteItem(id, t, _toastId);
                                        }}
                                    >
                                        <i className="icon fal fa-recycle"></i> {t("confirm")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ),
                    type: toast.TYPE.DARK,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        },
        [dispatch, executeDeleteItem]
    );

    // Clear school students execution
    const executeClearStudents = useCallback((id, t, _toastId) => {
        toast.update(_toastId, {
            render: () => (
                <div>
                    {" "}
                    <i className="fa-spin fas fa-spinner"></i> {t("Processing")}{" "}
                </div>
            ),
            autoClose: false,
            closeOnClick: false,
        });

        api.delete(`/DeleteSchoolStudents/${id}`)
            .then(() => {
                toast.update(_toastId, {
                    render: () => (
                        <div>
                            <div className="h5">
                                {" "}
                                <i className="icon fal fa-exclamation-triangle"></i>{" "}
                                {t("Pages.SchoolsControl.toast.DeleteStudents.Title")}
                            </div>{" "}
                            <div className="h6">
                                {t("Pages.SchoolsControl.toast.DeleteStudents.Body")}
                            </div>
                        </div>
                    ),
                    type: toast.TYPE.SUCCESS,
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                });
            })
            .catch(() => {
                toast.update(_toastId, {
                    render: () => (
                        <div>
                            <div className="h5">
                                {" "}
                                <i className="icon fal fa-wifi-slash"></i> {t("Error.NoConnection.Title")}
                            </div>{" "}
                            <div className="h6">{t("Error.NoConnection.Body")}</div>
                        </div>
                    ),
                    type: toast.TYPE.ERROR,
                    position: "bottom-left",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: false,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                });
            });
    }, []);

    // Clear school students dialog trigger
    const handleClearStudents = useCallback(
        (school, t) => {
            let _toastId = toast(t("Processing"), {
                position: "bottom-left",
                autoClose: false,
            });

            toast.update(_toastId, {
                render: () => (
                    <div>
                        <i className="fa-spin fas fa-spinner"></i>{" "}
                        {t("Pages.SchoolsControl.DeleteProcessing")}
                        <div className="row row-cols-2 mt-2">
                            <div className="col">
                                <button
                                    type="button"
                                    className="btn btn-danger btn-block"
                                    onClick={() => {
                                        toast.dismiss(_toastId);
                                    }}
                                >
                                    <i className="icon fal fa-trash-undo"></i> {t("undo")}
                                </button>
                            </div>
                            <div className="col">
                                <button
                                    type="button"
                                    className="btn btn-light btn-block"
                                    onClick={() => {
                                        executeClearStudents(school.Id, t, _toastId);
                                    }}
                                >
                                    <i className="icon fal fa-recycle"></i> {t("confirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                ),
                type: toast.TYPE.DARK,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
            });
        },
        [executeClearStudents]
    );

    // Primary action trigger dispatcher
    const handleActionTrigger = useCallback(
        (actionType, school, t) => {
            if (actionType === "adminInfo") {
                setActiveAdminUser(school);
                return;
            }

            if (actionType === "schoolInfo") {
                setActiveAdminUser(null);
                setSelectedSchoolId(school ? school.Id : null);
                setActiveModal("info");
                if (school && school.Id) {
                    dispatch(fetchSchoolDetails(school.Id));
                    fetchSchoolUsers(school.Id, t);
                }
                return;
            }

            setSelectedSchoolId(school ? school.Id : null);

            if (actionType === "add") {
                setActiveModal("add");
            } else if (actionType === "edit") {
                setActiveModal("edit");
            } else if (actionType === "bills") {
                setActiveModal("bills");
            } else if (actionType === "notes") {
                setActiveModal("notes");
            } else if (actionType === "info") {
                setActiveModal("info");
                if (school && school.Id) {
                    dispatch(fetchSchoolDetails(school.Id));
                    fetchSchoolUsers(school.Id, t);
                }
            } else if (actionType === "clearStudents") {
                setActiveModal(null);
                handleClearStudents(school, t);
            } else if (actionType === "delete") {
                setActiveModal(null);
                handleShowDelete(school, t);
            }
        },
        [dispatch, fetchSchoolUsers, handleClearStudents, handleShowDelete]
    );

    // Form Submission Actions
    const handleSubmitAdd = useCallback(
        (postData, _toastId, t) => {
            api.post("/Schools/", postData)
                .then((response) => {
                    if (response.data.data) {
                        dispatch(addAction(response.data.data));

                        toast.update(_toastId, {
                            render: () => (
                                <div>
                                    <div className="h5">
                                        {" "}
                                        <i className="icon fal fa-shield-check"></i>{" "}
                                        {t("Pages.SchoolsControl.toast.Add.Title")}
                                    </div>{" "}
                                    <div className="h6">{t("Pages.SchoolsControl.toast.Add.Body")}</div>
                                </div>
                            ),
                            type: toast.TYPE.SUCCESS,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });

                        handleCloseModal();
                        handleSearchSync();
                    } else {
                        toast.update(_toastId, {
                            render: () => (
                                <div>
                                    <div className="h5">
                                        {" "}
                                        <i className="icon fal fa-bug"></i>{" "}
                                        {t(
                                            "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title"
                                        )}
                                    </div>{" "}
                                    <div className="h6">
                                        {t(
                                            "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body"
                                        )}
                                    </div>
                                </div>
                            ),
                            type: toast.TYPE.ERROR,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });
                    }
                })
                .catch(() => {
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5">
                                    {" "}
                                    <i className="icon fal fa-wifi-slash"></i> {t("Error.NoConnection.Title")}
                                </div>{" "}
                                <div className="h6">{t("Error.NoConnection.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                });
        },
        [dispatch, handleCloseModal, handleSearchSync]
    );

    const handleSubmitEdit = useCallback(
        (schoolId, postData, _toastId, t) => {
            api.put(`/Schools/${schoolId}`, postData)
                .then((response) => {
                    if (response.data.data) {
                        dispatch(updateAction(schoolId, response.data.data));

                        toast.update(_toastId, {
                            render: () => (
                                <div>
                                    <div className="h5">
                                        {" "}
                                        <i className="icon fal fa-shield-check"></i>{" "}
                                        {t("Pages.SchoolsControl.toast.Update.Title")}
                                    </div>{" "}
                                    <div className="h6">{t("Pages.SchoolsControl.toast.Update.Body")}</div>
                                </div>
                            ),
                            type: toast.TYPE.SUCCESS,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });

                        handleCloseModal();
                        handleSearchSync();
                    } else {
                        toast.update(_toastId, {
                            render: () => (
                                <div>
                                    <div className="h5">
                                        {" "}
                                        <i className="icon fal fa-bug"></i>{" "}
                                        {t(
                                            "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Title"
                                        )}
                                    </div>{" "}
                                    <div className="h6">
                                        {t(
                                            "Pages.ProfileEdit.error_AllInformationIsRequired_toast.Body"
                                        )}
                                    </div>
                                </div>
                            ),
                            type: toast.TYPE.ERROR,
                            position: "bottom-left",
                            autoClose: 5000,
                            hideProgressBar: false,
                            closeOnClick: false,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                        });
                    }
                })
                .catch(() => {
                    toast.update(_toastId, {
                        render: () => (
                            <div>
                                <div className="h5">
                                    {" "}
                                    <i className="icon fal fa-wifi-slash"></i> {t("Error.NoConnection.Title")}
                                </div>{" "}
                                <div className="h6">{t("Error.NoConnection.Body")}</div>
                            </div>
                        ),
                        type: toast.TYPE.ERROR,
                        position: "bottom-left",
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                    });
                });
        },
        [dispatch, handleCloseModal, handleSearchSync]
    );

    return {
        activeModal,
        selectedSchool,
        schoolUsers,
        loadingSchoolUsers,
        activeAdminUser,
        setActiveAdminUser,
        handleCloseModal,
        handleActionTrigger,
        handleSubmitAdd,
        handleSubmitEdit,
    };
};

export default useSchoolActions;
